import React, { useState, useEffect } from 'react';
import { db1 } from '../firebase/config';
import { collection, addDoc, setDoc, serverTimestamp, doc } from 'firebase/firestore';
import { Container, Form, Button, Alert, Spinner, Card, Image, Row, Col, Tabs, Tab } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const UploadPage = () => {
  const [activeTab, setActiveTab] = useState('news');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    author: 'Al-Majd Media',
    excerpt: '',
    tags: '',
    duration: '',
    videoUrl: ''
  });
  const [images, setImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [isArabic, setIsArabic] = useState(true);
  const navigate = useNavigate();

  const translations = {
    'Upload Content': 'رفع المحتوى',
    'News Upload': 'رفع خبر',
    'Video Upload': 'رفع فيديو',
    'Gallery Upload': 'رفع معرض',
    'Publish your content': 'انشر محتواك',
    'Title *': 'العنوان *',
    'Enter title': 'أدخل العنوان',
    'Content *': 'المحتوى *',
    'Write your content': 'اكتب محتواك',
    'Category *': 'الفئة *',
    'Select a category': 'اختر فئة',
    'Author *': 'المؤلف *',
    'Enter author name': 'أدخل اسم المؤلف',
    'Excerpt': 'ملخص',
    'Brief description': 'وصف مختصر',
    'Tags': 'الكلمات الدلالية',
    'Comma-separated tags': 'كلمات دلالية مفصولة بفواصل',
    'Featured Image': 'الصورة الرئيسية',
    'Upload Featured Image': 'رفع الصورة الرئيسية',
    'Additional Images': 'صور إضافية',
    'Upload Images': 'رفع الصور',
    'Publish': 'نشر',
    'Publishing...': 'جاري النشر...',
    'Published Successfully!': 'تم النشر بنجاح!',
    'Your content has been published successfully.': 'تم نشر محتواك بنجاح.',
    'Please fill all required fields': 'يرجى ملء جميع الحقول المطلوبة',
    'Please add a featured image': 'يرجى إضافة صورة رئيسية',
    'Error saving content:': 'خطأ في حفظ المحتوى:',
    'Upload widget is still loading. Please try again.': 'أداة التحميل لا تزال تحمّل. يرجى المحاولة مرة أخرى.',
    'Duration': 'المدة',
    'Enter duration (e.g., 5:30)': 'أدخل المدة (مثال: 5:30)',
    'Video URL': 'رابط الفيديو',
    'Enter video URL': 'أدخل رابط الفيديو',
    'Breaking News': 'خبر عاجل',
    'Official Statement': 'بيان رسمي',
    'Field Report': 'تقرير ميداني',
    'Documentary': 'فيلم وثائقي',
    'Interview': 'مقابلة',
    'Field Photos': 'صور ميدانية',
    'Events': 'فعاليات'
  };

  const translateText = (text) => {
    return isArabic ? (translations[text] || text) : text;
  };

  const toggleLanguage = () => {
    setIsArabic(!isArabic);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    script.onload = () => setWidgetLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const openCloudinaryWidget = (isFeatured = false) => {
    if (!widgetLoaded) {
      setError(translateText("Upload widget is still loading. Please try again."));
      return;
    }

    window.cloudinary.openUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'drl2kb984',
        uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'salafcore',
        sources: ['local', 'url', 'camera'],
        multiple: !isFeatured && activeTab === 'gallery',
        clientAllowedFormats: ['jpg', 'jpeg', 'png'],
        maxImageFileSize: 5000000,
        maxFiles: isFeatured ? 1 : (activeTab === 'gallery' ? 20 : 5)
      },
      (error, result) => {
        if (!error && result && result.event === 'success') {
          if (isFeatured) {
            setFeaturedImage(result.info.secure_url);
          } else {
            if (activeTab === 'gallery') {
              setImages(prev => [...prev, {
                url: result.info.secure_url,
                caption: ''
              }]);
            } else {
              setImages(prev => [...prev, result.info.secure_url]);
            }
          }
        }
      }
    );
  };

  const removeImage = (index, isFeatured = false) => {
    if (isFeatured) {
      setFeaturedImage('');
    } else {
      setImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateImageCaption = (index, caption) => {
    if (activeTab === 'gallery') {
      setImages(prev => prev.map((img, i) => 
        i === index ? { ...img, caption } : img
      ));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.title || !formData.content || !formData.category || !formData.author) {
      setError(translateText('Please fill all required fields'));
      setLoading(false);
      return;
    }

    if (!featuredImage && activeTab !== 'video') {
      setError(translateText('Please add a featured image'));
      setLoading(false);
      return;
    }

    if (activeTab === 'video' && !formData.videoUrl) {
      setError('Please enter video URL');
      setLoading(false);
      return;
    }

    try {
      const token = uuidv4();
      let contentData = {};

      switch (activeTab) {
        case 'news':
          contentData = {
            title: formData.title,
            content: formData.content,
            category: formData.category,
            author: formData.author,
            excerpt: formData.excerpt,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
            featuredImage,
            additionalImages: images,
            token: token,
            status: 'published',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            views: 0,
            likes: 0,
            type: 'news'
          };
          await addDoc(collection(db1, 'blog_posts'), contentData);
          break;

        case 'video':
          contentData = {
            title: formData.title,
            content: formData.content,
            category: formData.category,
            author: formData.author,
            excerpt: formData.excerpt,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
            featuredImage: featuredImage || '/default-video-thumbnail.jpg',
            videoUrl: formData.videoUrl,
            duration: formData.duration,
            token: token,
            status: 'published',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            views: 0,
            likes: 0,
            type: 'video'
          };
          await addDoc(collection(db1, 'videos'), contentData);
          break;

        case 'gallery':
          contentData = {
            title: formData.title,
            content: formData.content,
            category: formData.category,
            author: formData.author,
            tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
            images: images,
            featuredImage: images[0]?.url || '',
            token: token,
            status: 'published',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            views: 0,
            likes: 0,
            type: 'gallery'
          };
          await addDoc(collection(db1, 'galleries'), contentData);
          break;
      }

      setSuccess(true);
      // Reset form
      setFormData({
        title: '',
        content: '',
        category: '',
        author: 'Al-Majd Media',
        excerpt: '',
        tags: '',
        duration: '',
        videoUrl: ''
      });
      setImages([]);
      setFeaturedImage('');

    } catch (err) {
      setError(`${translateText('Error saving content:')} ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)',
      padding: '0',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      direction: isArabic ? 'rtl' : 'ltr',
      position: 'relative',
      overflow: 'hidden',
      fontSize: '16px',
      lineHeight: '1.6',
      minHeight: '100vh'
    },
    content: {
      maxWidth: '1000px',
      margin: '0 auto',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      padding: '3rem 2rem',
      borderRadius: '20px',
      boxShadow: '0 15px 40px rgba(220, 53, 69, 0.08)',
      backdropFilter: 'blur(5px)',
      border: '1px solid rgba(220, 53, 69, 0.1)',
      marginBottom: '3rem',
      marginTop: '2rem'
    },
    header: {
      textAlign: 'center',
      color: '#1a1a1a',
      padding: '120px 20px 40px',
      background: `linear-gradient(135deg, 
        rgba(220, 53, 69, 0.05) 0%, 
        rgba(220, 53, 69, 0.1) 50%, 
        rgba(255, 255, 255, 0) 100%)`,
      position: 'relative',
      overflow: 'hidden'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1.5rem',
      background: 'linear-gradient(135deg, #dc3545 0%, #1a1a1a 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      textShadow: '0 5px 15px rgba(220, 53, 69, 0.2)',
      letterSpacing: '-1px',
      lineHeight: '1.2'
    },
    languageToggle: {
      position: 'fixed',
      top: '100px',
      right: isArabic ? 'auto' : '20px',
      left: isArabic ? '20px' : 'auto',
      zIndex: '1000'
    },
    toggleButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '10px 20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
      fontSize: '0.9rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    floatingShapes: {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
      pointerEvents: 'none'
    },
    shape: {
      position: 'absolute',
      background: 'rgba(220, 53, 69, 0.1)',
      borderRadius: '50%'
    },
    tabContent: {
      border: '1px solid rgba(220, 53, 69, 0.1)',
      borderTop: 'none',
      padding: '20px',
      borderRadius: '0 0 8px 8px'
    }
  };

  const getCategories = () => {
    switch (activeTab) {
      case 'news':
        return [
          { value: 'breaking_news', label: translateText('Breaking News') },
          { value: 'official_statement', label: translateText('Official Statement') },
          { value: 'field_report', label: translateText('Field Report') }
        ];
      case 'video':
        return [
          { value: 'documentary', label: translateText('Documentary') },
          { value: 'interview', label: translateText('Interview') },
          { value: 'field_report', label: translateText('Field Report') }
        ];
      case 'gallery':
        return [
          { value: 'field_photos', label: translateText('Field Photos') },
          { value: 'events', label: translateText('Events') },
          { value: 'portraits', label: 'Portraits' }
        ];
      default:
        return [];
    }
  };

  return (
    <div style={styles.container}>
      {/* Floating Background Shapes */}
      <div style={styles.floatingShapes}>
        <div style={{...styles.shape, width: '150px', height: '150px', top: '10%', left: '5%', background: 'rgba(220, 53, 69, 0.05)'}}></div>
        <div style={{...styles.shape, width: '120px', height: '120px', top: '60%', right: '10%', background: 'rgba(220, 53, 69, 0.08)'}}></div>
        <div style={{...styles.shape, width: '80px', height: '80px', bottom: '20%', left: '15%', background: 'rgba(220, 53, 69, 0.03)'}}></div>
      </div>

      {/* Language Toggle Button */}
      <div style={styles.languageToggle}>
        <button 
          onClick={toggleLanguage}
          style={styles.toggleButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1a1a1a';
            e.target.style.transform = 'translateY(-3px) scale(1.05)';
            e.target.style.boxShadow = '0 8px 20px rgba(26, 26, 26, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#dc3545';
            e.target.style.transform = 'translateY(0) scale(1)';
            e.target.style.boxShadow = '0 4px 15px rgba(220, 53, 69, 0.3)';
          }}
        >
          {isArabic ? 'ENGLISH' : 'العربية'}
        </button>
      </div>

      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.title}>{translateText('Upload Content')}</h1>
        <p style={{ color: '#dc3545', fontSize: '1.2rem' }}>
          {translateText('Publish your content')}
        </p>
      </header>

      {/* Main Content */}
      <div style={styles.content}>
        <Card className="shadow-lg" style={{ border: 'none', borderRadius: '12px' }}>
          <Card.Body style={{ padding: '30px' }}>
            <div className="text-center mb-4">
              <h2 style={{ color: '#dc3545', fontWeight: '700' }}>
                {translateText('Upload Content')}
              </h2>
              <p style={{ color: '#666' }}>
                {translateText('Publish your content')}
              </p>
            </div>

            {success ? (
              <Alert variant="light" style={{ 
                border: 'none',
                backgroundColor: 'rgba(255,255,255,0.9)',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                color: '#dc3545'
              }}>
                <div className="d-flex align-items-center">
                  <div className="rounded p-2 me-3" style={{ backgroundColor: '#dc3545' }}>
                    <i className="bi bi-check-circle-fill text-light fs-4"></i>
                  </div>
                  <div>
                    <h4 style={{ color: '#dc3545' }}>{translateText('Published Successfully!')}</h4>
                    <p style={{ color: '#dc3545' }}>{translateText('Your content has been published successfully.')}</p>
                  </div>
                </div>
              </Alert>
            ) : (
              <>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-3"
                  fill
                >
                  <Tab eventKey="news" title={translateText('News Upload')}>
                    <div style={styles.tabContent}>
                      <Form onSubmit={handleSubmit}>
                        {/* News form fields */}
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Title *')}</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            required
                            placeholder={translateText('Enter title')}
                            style={{ border: '1px solid rgba(220, 53, 69, 0.2)', borderRadius: '8px' }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Content *')}</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={8}
                            value={formData.content}
                            onChange={(e) => setFormData({...formData, content: e.target.value})}
                            required
                            placeholder={translateText('Write your content')}
                            style={{ border: '1px solid rgba(220, 53, 69, 0.2)', borderRadius: '8px', resize: 'none' }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Category *')}</Form.Label>
                          <Form.Select
                            value={formData.category}
                            onChange={(e) => setFormData({...formData, category: e.target.value})}
                            required
                            style={{ border: '1px solid rgba(220, 53, 69, 0.2)', borderRadius: '8px' }}
                          >
                            <option value="">{translateText('Select a category')}</option>
                            {getCategories().map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        {/* Add other form fields similarly for each tab */}
                        <Button 
                          type="submit" 
                          disabled={loading}
                          className="w-100 py-3"
                          style={{ 
                            backgroundColor: '#dc3545', 
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600'
                          }}
                        >
                          {loading ? (
                            <>
                              <Spinner as="span" size="sm" animation="border" className="me-2" />
                              {translateText('Publishing...')}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-send-fill me-2"></i>
                              {translateText('Publish')} {translateText('News Upload')}
                            </>
                          )}
                        </Button>
                      </Form>
                    </div>
                  </Tab>

                  <Tab eventKey="video" title={translateText('Video Upload')}>
                    <div style={styles.tabContent}>
                      <Form onSubmit={handleSubmit}>
                        {/* Video-specific fields */}
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Video URL *')}</Form.Label>
                          <Form.Control
                            type="url"
                            value={formData.videoUrl}
                            onChange={(e) => setFormData({...formData, videoUrl: e.target.value})}
                            required
                            placeholder={translateText('Enter video URL')}
                            style={{ border: '1px solid rgba(220, 53, 69, 0.2)', borderRadius: '8px' }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Duration')}</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.duration}
                            onChange={(e) => setFormData({...formData, duration: e.target.value})}
                            placeholder={translateText('Enter duration (e.g., 5:30)')}
                            style={{ border: '1px solid rgba(220, 53, 69, 0.2)', borderRadius: '8px' }}
                          />
                        </Form.Group>

                        {/* Add other common fields and submit button */}
                      </Form>
                    </div>
                  </Tab>

                  <Tab eventKey="gallery" title={translateText('Gallery Upload')}>
                    <div style={styles.tabContent}>
                      <Form onSubmit={handleSubmit}>
                        {/* Gallery-specific fields */}
                        <Form.Group className="mb-3">
                          <Form.Label style={{ color: '#dc3545', fontWeight: '600' }}>{translateText('Gallery Images *')}</Form.Label>
                          <div className="mb-3">
                            <Button 
                              variant="outline-primary" 
                              onClick={() => openCloudinaryWidget(false)}
                              type="button"
                              className="d-flex align-items-center me-2 mb-2"
                              style={{ 
                                color: '#dc3545', 
                                borderColor: '#dc3545',
                                borderRadius: '8px',
                                padding: '8px 16px'
                              }}
                            >
                              <i className="bi bi-cloud-arrow-up-fill me-2"></i>
                              {translateText('Upload Images')}
                            </Button>
                          </div>
                          
                          {images.length > 0 && (
                            <Row>
                              {images.map((img, index) => (
                                <Col key={index} lg={4} md={6} className="mb-3">
                                  <Card style={{ border: '1px solid rgba(220, 53, 69, 0.2)', borderRadius: '8px' }}>
                                    <div className="position-relative">
                                      <Image 
                                        src={img.url} 
                                        style={{ 
                                          height: '150px', 
                                          width: '100%',
                                          objectFit: 'cover', 
                                          borderTopLeftRadius: '8px',
                                          borderTopRightRadius: '8px'
                                        }}
                                      />
                                      <Button
                                        variant="light"
                                        size="sm"
                                        className="position-absolute top-0 end-0 m-1 p-0"
                                        style={{ 
                                          width: '24px', 
                                          height: '24px',
                                          borderRadius: '50%',
                                          border: '1px solid rgba(220, 53, 69, 0.2)',
                                          color: '#dc3545'
                                        }}
                                        onClick={() => removeImage(index)}
                                      >
                                        ×
                                      </Button>
                                    </div>
                                    <Card.Body className="p-2">
                                      <Form.Control
                                        type="text"
                                        placeholder="Image caption (optional)"
                                        value={img.caption}
                                        onChange={(e) => updateImageCaption(index, e.target.value)}
                                        size="sm"
                                        style={{ 
                                          border: '1px solid rgba(220, 53, 69, 0.2)',
                                          borderRadius: '4px',
                                          fontSize: '0.8rem'
                                        }}
                                      />
                                    </Card.Body>
                                  </Card>
                                </Col>
                              ))}
                            </Row>
                          )}
                        </Form.Group>

                        {/* Add other common fields and submit button */}
                      </Form>
                    </div>
                  </Tab>
                </Tabs>

                {error && (
                  <Alert variant="light" style={{ 
                    border: 'none',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    color: '#dc3545',
                    marginTop: '20px'
                  }}>
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                  </Alert>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default UploadPage;
